/**
 * LeadStateTimeline.tsx — the append-only lead_state_history audit timeline (LS8).
 *
 * Renders `LeadDetail.state_history` oldest→newest as an ordered list (`<ol>`,
 * so assistive tech announces position and order — WCAG 1.3.1). Each entry shows
 * the from→to status (reusing LeadStatusBadge), the actor, the localised
 * timestamp (Intl via formatDateTime — never a locale-less string), the
 * event_name, and the note/reason when present.
 *
 * The entries carry no stable unique id in the contract, so the key is the
 * (occurred_at, index) pair — index disambiguates same-instant appends.
 */

import { useTranslation } from 'react-i18next';
import type { LeadStateHistoryEntry } from '../../api/types';
import { EmptyState } from '../../components/EmptyState';
import { LeadStatusBadge } from './LeadStatusBadge';
import { formatDateTime } from './formatDateTime';

export interface LeadStateTimelineProps {
  entries: LeadStateHistoryEntry[];
  locale: string;
}

export const LeadStateTimeline = ({ entries, locale }: LeadStateTimelineProps) => {
  const { t } = useTranslation();

  if (entries.length === 0) {
    return <EmptyState data-testid="lead-timeline-empty" title={t('leadDetail.timeline.empty')} />;
  }

  return (
    <ol data-testid="lead-timeline" className="flex flex-col border-l border-ink-3/20 pl-4">
      {entries.map((entry) => (
        <li
          key={`${entry.occurred_at}-${entry.event_name}-${entry.from_status}-${entry.to_status}`}
          data-testid="lead-timeline-entry"
          className="relative pb-4 last:pb-0 before:absolute before:-left-[1.3125rem] before:top-1.5 before:h-2.5 before:w-2.5 before:rounded-full before:bg-ink-3"
        >
          <div className="flex flex-wrap items-center gap-2">
            {entry.from_status != null ? (
              <>
                <LeadStatusBadge status={entry.from_status} />
                <span aria-hidden="true" className="text-ink-3">
                  →
                </span>
              </>
            ) : null}
            <LeadStatusBadge status={entry.to_status} />
          </div>
          <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-meta text-ink-3">
            <div className="flex gap-1.5">
              <dt className="font-medium">{t('leadDetail.timeline.actor')}</dt>
              <dd className="text-ink-2">{t(`leadActor.${entry.actor}`)}</dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="font-medium">{t('leadDetail.timeline.when')}</dt>
              <dd className="text-ink-2">
                <time dateTime={entry.occurred_at}>
                  {formatDateTime(entry.occurred_at, locale)}
                </time>
              </dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="font-medium">{t('leadDetail.timeline.event')}</dt>
              <dd className="text-ink-2">{entry.event_name}</dd>
            </div>
          </dl>
          {entry.note != null && entry.note !== '' ? (
            <p data-testid="lead-timeline-note" className="mt-2 text-body text-ink-2">
              {t('leadDetail.timeline.note')}: {entry.note}
            </p>
          ) : null}
        </li>
      ))}
    </ol>
  );
};
